import api from './api';

/**
 * Trackmania Time Trials Service
 * Handles API calls for Trackmania time trial rankings and standings
 */
export const trackmaniaTimeTrialService = {
  /**
   * Calculate rankings for all maps in a Trackmania time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  calculateRankings: async (trialId) => {
    try {
      const response = await api.post(`/api/gulag/trackmania/time-trials/${trialId}/calculate-rankings`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Calculate rankings error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to calculate rankings'
      };
    }
  },

  /**
   * Sync from PyPlanet and calculate rankings in one operation
   * @param {string} trialId - Time trial ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  syncAndCalculateRankings: async (trialId) => {
    try {
      const response = await api.post(`/api/gulag/trackmania/time-trials/${trialId}/sync-and-calculate`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Sync and calculate error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to sync and calculate rankings'
      };
    }
  },

  /**
   * Get rankings for a specific Trackmania map
   * @param {string} trialId - Time trial ID
   * @param {string} mapUid - Map UID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  getMapRankings: async (trialId, mapUid) => {
    try {
      const response = await api.get(`/api/gulag/trackmania/time-trials/${trialId}/map/${mapUid}/rankings`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get map rankings error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load map rankings'
      };
    }
  },

  /**
   * Get overall standings for a Trackmania time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  getGlobalStandings: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/trackmania/time-trials/${trialId}/standings`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get global standings error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load global standings'
      };
    }
  },

  /**
   * Get rankings for all maps in a Trackmania time trial
   * @param {string} trialId - Time trial ID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  getAllMapRankings: async (trialId) => {
    try {
      const response = await api.get(`/api/gulag/trackmania/time-trials/${trialId}/map-rankings`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get all map rankings error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to load map rankings'
      };
    }
  },

  /**
   * Calculate rankings for a single Trackmania map
   * @param {string} trialId - Time trial ID
   * @param {string} mapUid - Map UID
   * @returns {Promise<{success: boolean, data?: object, error?: string}>}
   */
  calculateSingleMapRanking: async (trialId, mapUid) => {
    try {
      const response = await api.post(`/api/gulag/trackmania/time-trials/${trialId}/map/${mapUid}/calculate`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Calculate single map ranking error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to calculate map ranking'
      };
    }
  }
};

export default trackmaniaTimeTrialService;

